import iconsJson from "@/mappings/entity-icons.json"
import { EntityIconTypes } from "@/types/entity-icon-types"
import {
  GeoCategory,
  Place,
  PlaceCategory,
  PlacePlaceholder,
} from "@/types/place"

import {
  isAirport,
  isHotel,
  isLocation,
  isRestaurant,
} from "@/lib/entity-utils"
import { NamedIcon } from "@/components/icons/named-icon"
import { IconSvgProps } from "./icons/icon"
import type { IconName } from "./icons/icon-names"

type EntityIconProps = IconSvgProps &
  (
    | {
        type: EntityIconTypes
        place?: Place | PlacePlaceholder
        category?: PlaceCategory
      }
    | {
        type?: EntityIconTypes
        place: Place | PlacePlaceholder
        category?: PlaceCategory
      }
    | {
        category: PlaceCategory
        place?: Place | PlacePlaceholder
        type?: EntityIconTypes
      }
  )

type IconMapKey =
  | EntityIconTypes
  | PlaceCategory
  | GeoCategory
  | "location_country"

const iconMap = iconsJson as Record<IconMapKey, IconName>

function getIconName(typeOrCategory: IconMapKey): IconName | null {
  return iconMap[typeOrCategory]
}

export function EntityIcon({
  type,
  category,
  place,
  size = "relative",
  ...props
}: EntityIconProps) {
  const { name: _name, ...iconProps } = props
  let iconName: IconName | null = null

  // A hotel hosts stops that aren't stays — a meal in its restaurant, a festival in its hall. The
  // event's own type describes those, so the lodging place's categories don't speak for them.
  const nonStayAtHotel = !!type && type !== "hotel" && !!place && isHotel(place)

  if (
    place &&
    !nonStayAtHotel &&
    !isRestaurant(place) &&
    !isAirport(place) &&
    !isLocation(place) &&
    place.categories &&
    place.categories.length
  ) {
    for (const category of place.categories) {
      iconName = getIconName(category)
      if (iconName) break
    }
  }

  if (!iconName) {
    // deal with conflict with country event category
    if (place && isLocation(place) && place.category === "country") {
      iconName = getIconName("location_country")
    } else {
      iconName = getIconName(
        category ||
          type ||
          (place && isLocation(place) ? place?.category : place.type)
      )
    }
  }

  if (iconName) {
    return (
      <NamedIcon
        name={iconName as IconName}
        size={size}
        data-slot="entity-icon"
        {...iconProps}
      />
    )
  }
}
